// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NotarisDossierPage - V40-P1
 * Professioneel notarisdossier met 11 hoofdstukken.
 * Laadt alle beschikbare services. Geen schema-aanpassingen.
 */
import { useState, useEffect } from 'react'
import { ROUTES } from '@/lib/constants/routes'
import { useNavigate }   from 'react-router-dom'
import { Scale, Download, Printer, CheckCircle2, ArrowLeft, Mail, Copy } from 'lucide-react'
import type { ExportData, ExportSection } from '@/lib/export/wilsappExport'
import { Button }        from '@/components/ui/Button'
import { useAuthStore, useWizardStore } from '@/store'
import { getFirstProfile, getProfileAddress, loadOwnerDob } from '@/services/profile'
import { loadNotarial }  from '@/services/notarial'
import { loadTrustedPersons } from '@/services/trustedPerson'
import {
  loadHousing, loadPartner, loadChildren, loadBank,
  loadPension, loadInvestments, loadInsurance, loadSecondHome,
  loadTestamentDetails, loadLivingWillDetails, wizardLoad, pensionFundNames,
  type SecondHomeData,
} from '@/services/wizard'
import { loadMedicalWishes, loadFuneralWishes, loadDigitalEstate } from '@/services/wishes'



// ── Hulpfuncties ──────────────────────────────────────────────────────────────

function nl(v: string | undefined | null) { return v?.trim() || '' }
function ja(v: string | undefined | null) {
  if (v === 'yes' || v === 'true' || v === true as unknown) return 'Ja'
  if (v === 'no'  || v === 'false'|| v === false as unknown) return 'Nee'
  return v || '-'
}
function datum() { return new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) }

function line(label: string, value: string | undefined | null, missing = false): string {
  const v = nl(value)
  if (!v) return `  ${missing ? '❌' : '○'} ${label}: niet ingevuld`
  return `  ✅ ${label}: ${v}`
}

function section(title: string, lines: string[]): string {
  return ['', title, '─'.repeat(title.length), ...lines].join('\n')
}

// ── Open aandachtspunten ──────────────────────────────────────────────────────

interface Punt { label: string; ernst: '❌'|'⚠️'; reden: string }

// ── Hoofdcomponent ────────────────────────────────────────────────────────────

export function NotarisDossierPage() {
  const navigate   = useNavigate()
  const { userId } = useAuthStore()
  const { answers } = useWizardStore()

  const [isLoading,  setIsLoading]  = useState(true)
  const [firstName,  setFirstName]  = useState('')
  const [dossierText,setDossierText]= useState('')
  const [exportData,  setExportData]  = useState<ExportData | null>(null)
  const [generated,  setGenerated]  = useState(false)
  const [copied,     setCopied]     = useState(false)

  useEffect(() => {
    if (!userId) return
    async function load() {
      const [
        profile, notarial, partner, children, bank, pension,
        investments, housing, insurance, secondHome,
        testamentDetails, livingWillDetails,
        medical, funeral, digitalEstate, vpList, profileAddr, funeralCostsRaw, ownerDob,
      ] = await Promise.all([
        getFirstProfile(),
        loadNotarial(userId!),
        loadPartner(userId!),
        loadChildren(userId!),
        loadBank(userId!),
        loadPension(userId!),
        loadInvestments(userId!),
        loadHousing(userId!),
        loadInsurance(userId!),
        loadSecondHome(userId!),
        loadTestamentDetails(userId!),
        loadLivingWillDetails(userId!),
        loadMedicalWishes(userId!),
        loadFuneralWishes(userId!),
        loadDigitalEstate(userId!),
        loadTrustedPersons(userId!),
        getProfileAddress(userId!),
        wizardLoad<Record<string,unknown>>(userId!, 'funeralCosts'),
        loadOwnerDob(userId!),
      ])

      // Typed accessor voor funeralCosts (wizardLoad returnt Record<string,unknown>)
      const fc = funeralCostsRaw as {
        hasInsurance?: string; insurer?: string; insurerOther?: string
        policyNumber?: string; insuredAmount?: string; docsLocation?: string
        estimatedBudget?: string; financialContactName?: string
        financialContactRole?: string; financialContactPhone?: string
        financialContactEmail?: string; financialContactInstructions?: string
        financialContactCustom?: string
      } | null

      const name = profile?.firstName || '-'
      setFirstName(profile?.firstName || '')

      const activeVPs = (vpList || []).filter(v => v.confirmationStatus !== 'REVOKED')
      const primaryVP = activeVPs.find(v => v.role === 'PRIMARY')
      const reserveVP = activeVPs.find(v => v.role === 'RESERVE')

      // ── Aandachtspunten verzamelen ─────────────────────────────────────────
      const punten: Punt[] = []

      if (!notarial?.executeur?.name)
        punten.push({ label: 'Executeur niet ingevuld', ernst: '❌', reden: 'De notaris heeft een executeur nodig om uw nalatenschap te beheren en testamentaire wensen uit te voeren.' })
      if (!notarial?.executeur?.reserveName)
        punten.push({ label: 'Tweede executeur ontbreekt', ernst: '⚠️', reden: 'Als de hoofdexecuteur niet beschikbaar is, kan de nalatenschap vertraging oplopen.' })
      const hasVoogd = !!(children?.guardian?.name || notarial?.voogdij?.name ||
        children?.guardian?.appointed === 'testament')
      if (answers.hasChildren && !hasVoogd)
        punten.push({ label: 'Voogd voor kinderen niet ingevuld', ernst: '❌', reden: 'Bij overlijden van beide ouders beslist de rechter over voogdij als dit niet is vastgelegd.' })
      if (answers.hasBusiness && !notarial?.ondernemingWens)
        punten.push({ label: 'Onderneming: opvolgingswens ontbreekt', ernst: '❌', reden: 'Zonder duidelijke instructies kan de onderneming onbeheerd achterblijven.' })
      if (answers.hasOwnHome && !housing?.mortgageNumber)
        punten.push({ label: 'Hypotheeknummer ontbreekt', ernst: '⚠️', reden: 'De notaris heeft dit nodig voor eigendomsoverdracht en hypotheekafwikkeling.' })
      if (!pension?.hasPension)
        punten.push({ label: 'Pensioeninformatie ontbreekt', ernst: '⚠️', reden: 'Nabestaanden kunnen anders pensioen- of ANW-rechten mislopen.' })
      if (!medical?.proxyDecisionMaker && !['primary_vp','reserve_vp'].includes(medical?.proxySource || ''))
        punten.push({ label: 'Medisch vertegenwoordiger niet ingevuld', ernst: '⚠️', reden: 'Zonder aanwijzing beslist de wet of de rechter wie medische beslissingen neemt.' })
      // V40C-P1: proxy aangewezen maar geen levenstestament
      const hasProxyDossier = !!(medical?.proxySource && medical.proxySource !== '') || !!(medical?.proxyDecisionMaker)
      if (hasProxyDossier && medical?.livingWillExists !== 'yes')
        punten.push({ label: 'Medisch vertegenwoordiger aangewezen maar niet opgenomen in levenstestament', ernst: '⚠️', reden: 'Artsen en zorginstellingen kunnen bij wilsonbekwaamheid vragen naar formele vastlegging via een levenstestament.' })
      if (!testamentDetails?.notarisName && answers.hasTestament)
        punten.push({ label: 'Notarisgegevens testament ontbreken', ernst: '⚠️', reden: 'Nabestaanden en de uitvoerend notaris moeten weten waar het testament is opgemaakt.' })
      if (answers.hasSecondHome && !(secondHome as SecondHomeData)?.localContact?.phone)
        punten.push({ label: 'Lokaal contact tweede woning ontbreekt', ernst: '⚠️', reden: 'Bij een buitenlandse woning is een lokaal contactpersoon essentieel voor beheer en verkoop.' })

      // ── Logische controle: dubbele aanwijzingen ────────────────────────────
      const exNaam = nl(notarial?.executeur?.name)
      if (exNaam && exNaam.toLowerCase() === nl(notarial?.executeur?.reserveName).toLowerCase())
        punten.push({ label: 'Eerste en tweede executeur zijn dezelfde persoon', ernst: '⚠️', reden: 'Een tweede executeur is alleen zinvol als deze afwijkt van de eerste; controleer of dit klopt.' })
      const voogdN  = nl(children?.guardian?.name || notarial?.voogdij?.name)
      const voogdRN = nl(children?.guardian?.reserveName || notarial?.voogdij?.reserveName)
      if (voogdN && voogdN.toLowerCase() === voogdRN.toLowerCase())
        punten.push({ label: 'Eerste en tweede voogd zijn dezelfde persoon', ernst: '⚠️', reden: 'Een reservevoogd is alleen zinvol als deze afwijkt van de eerste; controleer of dit klopt.' })
      if (primaryVP && reserveVP && (primaryVP.id === reserveVP.id ||
          (nl(primaryVP.firstName).toLowerCase() === nl(reserveVP.firstName).toLowerCase() &&
           nl(primaryVP.relationship).toLowerCase() === nl(reserveVP.relationship).toLowerCase())))
        punten.push({ label: 'Eerste en tweede vertrouwenspersoon zijn dezelfde persoon', ernst: '⚠️', reden: 'Een tweede vertrouwenspersoon is alleen zinvol als deze afwijkt van de eerste; controleer of dit klopt.' })

      // ── Dossier opbouwen ───────────────────────────────────────────────────
      const partnerNaam = partner?.hasPartner === 'yes'
        ? [partner.name, partner.lastName].filter(Boolean).join(' ') : 'Geen partner'

      const kinderenLijst = children?.hasChildren === 'yes' && children.children?.length
        ? children.children.map((c, i) => `  ${i + 1}. ${[c.name, c.lastName].filter(Boolean).join(' ')}${c.dob ? ` (geb. ${c.dob})` : ''}`).join('\n')
        : '  Geen kinderen opgegeven'

      const ch1 = section('HOOFDSTUK 1 - PERSOONLIJKE SITUATIE', [
        line('Naam',        name),
        line('Adres',       [profileAddr?.street, profileAddr?.houseNumber, profileAddr?.city].filter(Boolean).join(' ')),
        ...(nl(ownerDob) ? [line('Geboortedatum', ownerDob)] : []),
        line('Relatievorm', partner?.relationForm || (partner?.hasPartner === 'yes' ? 'Ingevuld' : 'Geen partner')),
        notarial?.huwelijkseVoorwaarden === 'yes' ? [
          `  ✅ Huwelijkse voorwaarden: Ja`,
          ...(notarial.huwelijkseVoorwaardenNotaris ? [line('  Notaris', notarial.huwelijkseVoorwaardenNotaris)] : []),
          ...(notarial.huwelijkseVoorwaardenDatum   ? [line('  Datum',   notarial.huwelijkseVoorwaardenDatum)] : []),
          ...(notarial.huwelijkseVoorwaardenLocatie  ? [line('  Locatie', notarial.huwelijkseVoorwaardenLocatie)] : []),
        ].join('\n') : notarial?.huwelijkseVoorwaarden === 'no' ? `  Huwelijkse voorwaarden: Nee` : `  ○ Huwelijkse voorwaarden: niet beantwoord`,
      ])

      const ch2 = section('HOOFDSTUK 2 - FAMILIE EN ERFGENAMEN', [
        line('Partner',    partnerNaam),
        ...(partner?.hasPartner === 'yes' ? [
          line('  Telefoon partner', partner.phone || ''),
          line('  E-mail partner',   partner.email || ''),
        ] : []),
        '',
        '  Kinderen:',
        kinderenLijst,
        '',
        ...(() => {
          // V40-P2: children.guardian is primaire bron; notarial.voogdij als fallback
          const g = children?.guardian
          const voogdNaam = g?.name || notarial?.voogdij?.name || ''
          const voogdTel  = g?.phone || notarial?.voogdij?.phone || ''
          const voogdRel  = g?.relation || notarial?.voogdij?.relation || ''
          if (g?.appointed === 'testament') {
            return [
              `  ✅ Voogdij: opgenomen in testament`,
              ...(g.testamentNotaris  ? [line('  Notaris', g.testamentNotaris)] : []),
              ...(g.testamentYear     ? [line('  Jaar',    g.testamentYear)] : []),
              ...(g.testamentLocation ? [line('  Locatie', g.testamentLocation)] : []),
            ]
          }
          if (g?.appointed === 'no') {
            return [`  ⚠️  Voogdij: nog niet geregeld - aandachtspunt voor notaris`]
          }
          if (voogdNaam) {
            const reserveVoogdNaam = g?.reserveName || notarial?.voogdij?.reserveName || ''
            const reserveVoogdRel  = g?.reserveRelation || notarial?.voogdij?.reserveRelation || ''
            const reserveVoogdTel  = g?.reservePhone || notarial?.voogdij?.reservePhone || ''
            const effectiefRel = voogdRel === 'Anders' ? (notarial?.voogdij?.relationOther || voogdRel) : voogdRel
            const effectiefResRel = reserveVoogdRel === 'Anders' ? (notarial?.voogdij?.reserveRelationOther || reserveVoogdRel) : reserveVoogdRel
            const lines = [line('Voogd kinderen', `${voogdNaam}${effectiefRel ? ' (' + effectiefRel + ')' : ''}${voogdTel ? ' - ' + voogdTel : ''}`)]
            if (reserveVoogdNaam) lines.push(line('Tweede voogd', `${reserveVoogdNaam}${effectiefResRel ? ' (' + effectiefResRel + ')' : ''}${reserveVoogdTel ? ' - ' + reserveVoogdTel : ''}`))
            if (g?.notarialNotes) lines.push(line('  Toelichting', g.notarialNotes))
            return lines
          }
          return [`  ❌ Voogd kinderen: niet ingevuld`]
        })(),
        '',
        line('Eerste vertrouwenspersoon', primaryVP ? `${primaryVP.firstName} (${primaryVP.relationship})` : ''),
        line('Tweede vertrouwenspersoon',  reserveVP ? `${reserveVP.firstName} (${reserveVP.relationship})` : ''),
        ...(primaryVP ? [
          '',
          '  Rol:  Vertrouwenspersoon',
          '  Doel: Toegang tot uitsluitend de door de gebruiker vrijgegeven',
          '        onderdelen van WilsApp. Dit is geen executeur, erfgenaam,',
          '        gevolmachtigde of voogd.',
        ] : []),
      ])

      const ch3 = section('HOOFDSTUK 3 - TESTAMENTWENSEN', [
        `  Testament gewenst: ${answers.hasTestament ? 'Ja' : answers.hasTestament === false ? 'Nee' : 'Niet beantwoord'}`,
        ...(testamentDetails?.notarisName ? [
          line('Notaris',   testamentDetails.notarisName),
          line('Kantoor',   testamentDetails.notarisCity || ''),
          line('Jaar',      testamentDetails.year || ''),
          line('Locatie document', testamentDetails.documentLocation || ''),
        ] : [
          `  ○ Notarisgegevens testament: niet ingevuld`,
        ]),
        '',
        notarial?.executeur?.name
          ? line('Executeur', `${notarial.executeur.name} (${notarial.executeur.relation || 'relatie onbekend'}) - ${notarial.executeur.phone || 'geen telefoon'}`)
          : `  ❌ Executeur: niet ingevuld`,
        notarial?.executeur?.reserveName
          ? line('Tweede executeur', `${notarial.executeur.reserveName} (${notarial.executeur.reserveRelation || ''}) - ${notarial.executeur.reservePhone || ''}`)
          : `  ⚠️  Tweede executeur: niet ingevuld`,
        '',
        notarial?.erfgenaanWensen
          ? `  ✅ Erfgenamenwensen:\n${notarial.erfgenaanWensen.split('\n').map(l => '    ' + l).join('\n')}`
          : `  ○ Erfgenamenwensen: niet ingevuld`,
        '',
        ...((notarial?.bezittingen?.length ?? 0) > 0
          ? [`  ✅ Bezittingenverdeling (${notarial!.bezittingen.length} item${notarial!.bezittingen.length > 1 ? 's' : ''}):`,
             ...notarial!.bezittingen.map(b => `    • ${b.bezitting} → ${b.persoon}${b.opmerking ? ' (' + b.opmerking + ')' : ''}`)]
          : [`  ○ Bezittingenverdeling: niet ingevuld`]
        ),
        '',
        ...((notarial?.goedeDoelenLijst?.length ?? 0) > 0
          ? [`  ✅ Goede doelen (${notarial!.goedeDoelenLijst.length}):`,
             ...notarial!.goedeDoelenLijst.map(g => `    • ${g.organisatie}: ${g.bedragOfPct}${g.opmerking ? ' - ' + g.opmerking : ''}`)]
          : [`  ○ Goede doelen: niet ingevuld`]
        ),
      ])

      const ch4 = section('HOOFDSTUK 4 - LEVENSTESTAMENT', [
        `  Levenstestament gewenst: ${answers.hasLivingWill ? 'Ja' : answers.hasLivingWill === false ? 'Nee' : 'Niet beantwoord'}`,
        ...(livingWillDetails?.notarisName ? [
          line('Notaris',   livingWillDetails.notarisName),
          line('Kantoor',   livingWillDetails.notarisCity || ''),
          line('Jaar',      livingWillDetails.year || ''),
        ] : [
          `  ○ Notarisgegevens levenstestament: niet ingevuld`,
        ]),
        // V41: Volmachthouder
        ...(livingWillDetails?.proxyName ? [
          '',
          line('Volmachthouder', `${livingWillDetails.proxyName}${livingWillDetails.proxyRelation ? ' (' + livingWillDetails.proxyRelation + ')' : ''}`),
          ...(livingWillDetails.proxyPhone ? [line('  Telefoon', livingWillDetails.proxyPhone)] : []),
          ...(livingWillDetails.proxyEmail ? [line('  E-mail',   livingWillDetails.proxyEmail)] : []),
          ...(livingWillDetails.proxyScope ? [line('  Bevoegdheid', livingWillDetails.proxyScope === 'financial' ? 'Financieel' : livingWillDetails.proxyScope === 'medical' ? 'Medisch' : livingWillDetails.proxyScope === 'both' ? 'Financieel én medisch' : livingWillDetails.proxyScope)] : []),
        ] : [
          `  ○ Volmachthouder: niet ingevuld`,
        ]),
      ])

      const proxyName = medical?.proxySource === 'primary_vp' ? (primaryVP?.firstName || 'Eerste vertrouwenspersoon')
        : medical?.proxySource === 'reserve_vp' ? (reserveVP?.firstName || 'Reserve VP')
        : medical?.proxyDecisionMaker || ''

      const hasProxy = !!(proxyName)
      const hasLivingWill = medical?.livingWillExists === 'yes'

      const ch5 = section('HOOFDSTUK 5 - MEDISCHE VERTEGENWOORDIGING', [
        hasProxy
          ? line('Medisch vertegenwoordiger', proxyName)
          : `  ❌ Medisch vertegenwoordiger: niet ingevuld`,
        ...(hasProxy && medical?.proxyPhone ? [line('  Telefoon', medical.proxyPhone)] : []),
        ...(hasProxy && medical?.proxyEmail ? [line('  E-mail',   medical.proxyEmail)] : []),
        ...(hasProxy ? [
          `  ✓ Opgenomen in notarisdossier`,
          hasLivingWill
            ? `  ✅ Levenstestament: vastgelegd`
            : `  ⚠️  Levenstestament: niet juridisch vastgelegd via levenstestament`,
        ] : []),
        // V41: reserve medisch vertegenwoordiger
        ...(medical?.reserveProxyName ? [
          line('Tweede vertegenwoordiger', `${medical.reserveProxyName}${medical.reserveProxyPhone ? ' - ' + medical.reserveProxyPhone : ''}`),
          ...(medical.reserveProxyEmail ? [line('  E-mail reserve', medical.reserveProxyEmail)] : []),
        ] : medical?.reserveProxySource === 'reserve_vp' ? [
          `  ✅ Tweede vertegenwoordiger: Tweede vertrouwenspersoon`,
        ] : [
          `  ○ Tweede vertegenwoordiger: niet ingevuld`,
        ]),
        '',
        `  Reanimatiewens: ${medical?.resuscitation === 'yes' ? 'Ja, reanimeer' : medical?.resuscitation === 'no' ? 'Nee, niet reanimeren (DNAR)' : medical?.resuscitation === 'discuss' ? 'Bespreek met arts' : 'Niet beantwoord'}`,
        `  Wilsverklaring: ${medical?.livingWillExists === 'yes' ? 'Aanwezig' : medical?.livingWillExists === 'pending' ? 'In voorbereiding' : medical?.livingWillExists === 'no' ? 'Niet aanwezig' : 'Niet beantwoord'}`,
        ...(medical?.livingWillLocation ? [line('  Locatie wilsverklaring', medical.livingWillLocation)] : []),
        '',
        line('Huisarts', medical?.gpName || ''),
        ...(medical?.gpPractice ? [line('  Praktijk', medical.gpPractice)] : []),
        ...(medical?.gpPhone    ? [line('  Telefoon', medical.gpPhone)] : []),
      ])

      const ch6 = section('HOOFDSTUK 6 - ONDERNEMING', [
        `  Onderneming aanwezig: ${answers.hasBusiness ? 'Ja' : 'Nee / niet beantwoord'}`,
        ...(answers.hasBusiness ? [
          notarial?.ondernemingWens     ? line('Opvolgingswens',  notarial.ondernemingWens) : `  ❌ Opvolgingswens: niet ingevuld`,
          notarial?.ondernemingAfwikkel ? line('Afwikkeling',     notarial.ondernemingAfwikkel) : '',
          notarial?.ondernemingBoekhouder ? line('Boekhouder',    notarial.ondernemingBoekhouder) : '',
          notarial?.kvkNummer ? line('KvK-nummer', notarial.kvkNummer) : `  ○ KvK-nummer: niet ingevuld`,
        ...(notarial?.kvkVestigingsadres ? [line('Vestigingsadres', notarial.kvkVestigingsadres)] : []),
        ...(notarial?.kvkOpvolger ? [line('Opvolger/contactpersoon', notarial.kvkOpvolger)] : []),
        ] : []),
      ])

      const ch7Lines: string[] = []
      if (answers.hasOwnHome) {
        ch7Lines.push(`  ✅ Koopwoning aanwezig`)
        if (housing?.mortgageProvider) ch7Lines.push(line('  Hypotheekverstrekker', housing.mortgageProvider))
        if (housing?.mortgageNumber)   ch7Lines.push(line('  Hypotheeknummer', housing.mortgageNumber))
        else ch7Lines.push(`  ⚠️  Hypotheeknummer: niet ingevuld`)
        if (housing?.hasOrv) ch7Lines.push(`  Overlijdensrisicoverzekering: ${ja(housing.hasOrv)}`)
      } else {
        ch7Lines.push(`  Geen koopwoning opgegeven`)
      }
      if (answers.hasSecondHome) {
        const sh = secondHome as SecondHomeData | null
        ch7Lines.push('')
        ch7Lines.push(`  ✅ Tweede woning / buitenlands vastgoed`)
        if (sh?.country) ch7Lines.push(line('  Land', sh.country === 'IT' ? 'Italië' : sh.country === 'ES' ? 'Spanje' : sh.country === 'FR' ? 'Frankrijk' : sh.country))
        if (sh?.city)   ch7Lines.push(line('  Plaats', sh.city))
        if (sh?.localContact?.name)  ch7Lines.push(line('  Lokaal contact', sh.localContact.name))
        if (sh?.localContact?.phone) ch7Lines.push(line('  Telefoon contact', sh.localContact.phone))
        if (sh?.mortgageProvider)    ch7Lines.push(line('  Hypotheekverstrekker', sh.mortgageProvider))
      }
      const ch7 = section('HOOFDSTUK 7 - VASTGOED', ch7Lines.length ? ch7Lines : [`  Geen vastgoed opgegeven`])

      const bankLines: string[] = []
      if (bank?.accounts?.length) {
        bank.accounts.forEach((acc, i) => {
          bankLines.push(`  ${i + 1}. ${acc.bankName || 'Bank'} - ${acc.accountType || 'type onbekend'}`)
          if (acc.iban)     bankLines.push(`     IBAN: ${acc.iban}`)
          if (acc.coHolder) bankLines.push(`     Medehouder: ${acc.coHolder}`)
        })
      } else {
        bankLines.push(`  ○ Bankrekeningen: niet ingevuld`)
      }
      if (investments?.hasInvestments === 'yes' && investments.investments?.length) {
        bankLines.push('')
        bankLines.push(`  ✅ Beleggingen (${investments.investments.length}):`)
        investments.investments.forEach((inv, i) => {
          const broker = inv.brokerName === 'Anders' ? inv.brokerNameOther : inv.brokerName
          bankLines.push(`  ${i + 1}. ${broker || '-'} - ${inv.investmentType || 'type onbekend'}${inv.hasLeverage === 'yes' ? ' ⚠️ hefboom/opties' : ''}`)
        })
      } else if (investments?.hasInvestments === 'no') {
        bankLines.push(`  Geen beleggingen`)
      } else {
        bankLines.push(`  ○ Beleggingen: niet beantwoord`)
      }
      const ch8 = section('HOOFDSTUK 8 - BANKEN EN BELEGGINGEN', bankLines)

      // ── HOOFDSTUK 9A - UITVAARTWENSEN ──────────────────────────────────────
      const uitvaartLines: string[] = []
      if (funeral?.type) {
        const typeLabel = funeral.type === 'burial' ? 'Begraving' : funeral.type === 'cremation' ? 'Crematie' : funeral.type === 'natural' ? 'Natuurbegraving' : funeral.type
        uitvaartLines.push(line('Type uitvaart', typeLabel))
      } else {
        uitvaartLines.push(`  ○ Type uitvaart: niet ingevuld`)
      }
      if (funeral?.locationWish)  uitvaartLines.push(line('Locatiewens', funeral.locationWish))
      if (funeral?.locationVenue) uitvaartLines.push(line('Locatie',     funeral.locationVenue))
      if (funeral?.ceremonyType) {
        const cLabel = funeral.ceremonyType === 'religious' ? 'Religieus' : funeral.ceremonyType === 'civil' ? 'Burgerlijk' : funeral.ceremonyType === 'private' ? 'Prive' : funeral.ceremonyType === 'none' ? 'Geen ceremonie' : funeral.ceremonyType
        uitvaartLines.push(line('Ceremonietype', cLabel))
      }
      if (funeral?.ceremonyWishes)   uitvaartLines.push(line('Ceremoniewensen', funeral.ceremonyWishes))
      if (funeral?.funeralHome)      uitvaartLines.push(line('Uitvaartverzorger', funeral.funeralHome))
      if (funeral?.funeralHomePhone) uitvaartLines.push(line('  Telefoon verzorger', funeral.funeralHomePhone))
      if (funeral?.music)            uitvaartLines.push(line('Muziekwensen',    funeral.music))
      if (funeral?.flowers)          uitvaartLines.push(line('Bloemenwens',     funeral.flowers))
      if (funeral?.clothing)         uitvaartLines.push(line('Kleding',         funeral.clothing))
      if (funeral?.specialRequests)  uitvaartLines.push(line('Bijzondere wensen', funeral.specialRequests))
      if (!uitvaartLines.length)     uitvaartLines.push(`  ○ Uitvaartwensen: niet ingevuld`)

      // ── Uitvaartverzekering & Kosten ───────────────────────────────────────
      uitvaartLines.push('')
      uitvaartLines.push('  ── Uitvaartverzekering & Kosten ──')
      if (fc?.hasInsurance === 'yes') {
        const insurer = fc.insurer === 'Anders' ? fc.insurerOther : fc.insurer
        uitvaartLines.push(line('Uitvaartverzekering', 'Ja'))
        if (insurer)         uitvaartLines.push(line('  Verzekeraar',   insurer || ''))
        if (fc.policyNumber) uitvaartLines.push(line('  Polisnummer',   fc.policyNumber))
        if (fc.insuredAmount)uitvaartLines.push(line('  Verzekerd bedrag', fc.insuredAmount))
        if (fc.docsLocation) uitvaartLines.push(line('  Polis bewaard', fc.docsLocation))
      } else if (fc?.hasInsurance === 'no') {
        uitvaartLines.push(`  Uitvaartverzekering: Nee`)
      } else {
        uitvaartLines.push(`  ○ Uitvaartverzekering: niet beantwoord`)
      }
      if (fc?.estimatedBudget) uitvaartLines.push(line('Indicatie budget', fc.estimatedBudget))
      // Financieel contactpersoon uitvaart
      const fcName = fc?.financialContactName || fc?.financialContactCustom
      if (fcName) {
        uitvaartLines.push('')
        uitvaartLines.push(line('Financieel contactpersoon uitvaart', fcName))
        if (fc?.financialContactRole)  uitvaartLines.push(line('  Functie',  fc.financialContactRole))
        if (fc?.financialContactPhone) uitvaartLines.push(line('  Telefoon', fc.financialContactPhone))
        if (fc?.financialContactEmail) uitvaartLines.push(line('  E-mail',   fc.financialContactEmail))
        if (fc?.financialContactInstructions) uitvaartLines.push(line('  Instructies', fc.financialContactInstructions))
      }
      const ch9a = section('HOOFDSTUK 9A - UITVAARTWENSEN EN VERZEKERING', uitvaartLines)

      const pensionLines: string[] = []
      if (pension?.hasPension) {
        const names = pensionFundNames(pension)
        pensionLines.push(line(names.length > 1 ? 'Pensioenfondsen' : 'Pensioenfonds', names.join(', ') || ''))
        pensionLines.push(`  Partnerpensioen: ${ja(pension.hasPartnerPension)}`)
        pensionLines.push(`  Wezenpensioen: ${ja(pension.hasSurvivorPension)}`)
        if (pension.hasPensionOverview) pensionLines.push(`  Pensioenoverzicht opgeslagen: ${ja(pension.hasPensionOverview)}`)
        if ((pension.pensionOverviewLocation ?? '').trim()) pensionLines.push(line('  Pensioenoverzicht te vinden', (pension.pensionOverviewLocation ?? '').trim()))
        if (pension.hasSupplementaryPension) pensionLines.push(`  Aanvullend pensioen / lijfrente: ${ja(pension.hasSupplementaryPension)}`)
        if (pension.hasAnwProvision) pensionLines.push(`  Nabestaandenvoorziening / ANW-hiaatverzekering: ${ja(pension.hasAnwProvision)}`)
      } else {
        pensionLines.push(`  ○ Pensioeninformatie: niet ingevuld`)
      }
      const lifeIns  = insurance?.insurances?.filter(i => ['Levensverzekering','Overlijdensrisicoverzekering'].includes(i.type))
      const funeralIns = insurance?.insurances?.filter(i => i.type === 'Uitvaartverzekering')
      if (lifeIns?.length) {
        pensionLines.push('')
        pensionLines.push(`  ✅ Levensverzekering / ORV (${lifeIns.length}):`)
        lifeIns.forEach(ins => {
          const insurer = ins.insurer === 'Anders' ? ins.insurerOther : ins.insurer
          pensionLines.push(`    • ${ins.type} bij ${insurer || '-'}${ins.policyNumber ? ' - polis: ' + ins.policyNumber : ''}`)
        })
      }
      if (funeralIns?.length) {
        pensionLines.push('')
        pensionLines.push(`  ✅ Uitvaartverzekering (${funeralIns.length}) - zie ook Hoofdstuk 9A:`)
        funeralIns.forEach(ins => {
          const insurer = ins.insurer === 'Anders' ? ins.insurerOther : ins.insurer
          pensionLines.push(`    • ${insurer || '-'}${ins.policyNumber ? ' - polis: ' + ins.policyNumber : ''}`)
        })
      }
      const ch9 = section('HOOFDSTUK 9 - PENSIOEN EN VERZEKERINGEN', pensionLines)

      const digLines: string[] = []
      const de = digitalEstate
      if (de?.hasSmartphone === 'yes') {
        digLines.push(line('Smartphone', [de.phoneBrand, de.phoneModel].filter(Boolean).join(' ')))
        if (de.phoneProvider)      digLines.push(line('  Provider', de.phoneProvider))
      }
      if (de?.authenticatorApp && de.authenticatorApp !== 'none') {
        const appName = de.authenticatorApp === 'microsoft' ? 'Microsoft Authenticator' : de.authenticatorApp === 'google' ? 'Google Authenticator' : de.authenticatorApp === 'apple' ? 'Apple' : de.authenticatorApp
        digLines.push(line('Authenticator-app', appName))
      }
      if (de?.passwordManagerApp && de.passwordManagerApp !== 'none') {
        const pmName = de.passwordManagerApp === '1password' ? '1Password' : de.passwordManagerApp === 'apple' ? 'Apple Wachtwoorden' : de.passwordManagerApp === 'google' ? 'Google Password Manager' : de.passwordManagerApp
        digLines.push(line('Wachtwoordmanager', pmName))
        if (de.passwordManagerLocation) digLines.push(line('  Locatie hoofdwachtwoord', de.passwordManagerLocation))
      }
      if (!digLines.length) digLines.push(`  ○ Digitale nalatenschap: niet ingevuld`)
      const ch10 = section('HOOFDSTUK 10 - DIGITALE NALATENSCHAP', digLines)

      // Prioriteit: hoog = testament/executeur/voogd/medisch vertegenwoordiger/onderneming,
      // midden = hypotheek/pensioen/tweede (buitenlands) woning, laag = overig.
      const HOOG   = /executeur niet|voogd|medisch vertegenwoordiger niet|notarisgegevens testament|onderneming/i
      const MIDDEN = /hypotheek|pensioen|tweede woning|buitenlands/i
      const prioOf = (p: Punt): 'hoog' | 'midden' | 'laag' =>
        HOOG.test(p.label) ? 'hoog' : MIDDEN.test(p.label) ? 'midden' : 'laag'
      const puntRegel = (p: Punt) => `  ${p.ernst} ${p.label}\n     Waarom belangrijk? ${p.reden}`
      const groep = (titel: string, lijst: Punt[]) =>
        lijst.length ? ['', titel, ...lijst.map(puntRegel)] : []
      const ch11Lines: string[] = punten.length
        ? [
            ...groep('▸ HOGE PRIORITEIT', punten.filter(p => prioOf(p) === 'hoog')),
            ...groep('▸ GEMIDDELDE PRIORITEIT', punten.filter(p => prioOf(p) === 'midden')),
            ...groep('▸ LAGE PRIORITEIT', punten.filter(p => prioOf(p) === 'laag')),
          ]
        : [`  ✅ Geen kritieke open punten gevonden`]
      const ch11 = section('HOOFDSTUK 11 - OPEN AANDACHTSPUNTEN', ch11Lines)

      // ── Samenvatting ──────────────────────────────────────────────────────
      // ── Voorbereidingsstatus (uitsluitend bestaande gegevens) ──────────────
      const statusChecks: { label: string; ok: boolean }[] = [
        { label: 'Testament',                 ok: !!answers.hasTestament },
        { label: 'Executeur',                 ok: !!nl(notarial?.executeur?.name) },
        { label: 'Medisch vertegenwoordiger', ok: !!(medical?.proxyDecisionMaker || medical?.proxySource) },
        { label: 'Vertrouwenspersoon',        ok: !!primaryVP },
        { label: 'Levenstestament',           ok: !!answers.hasLivingWill },
        ...(children?.hasChildren === 'yes'
          ? [{ label: 'Voogdij', ok: !!(nl(children?.guardian?.name) || nl(notarial?.voogdij?.name) || children?.guardian?.appointed === 'testament') }]
          : []),
      ]
      const statusPct = Math.round(100 * statusChecks.filter(c => c.ok).length / statusChecks.length)
      const statusAandacht = statusChecks.filter(c => !c.ok).map(c => c.label)

      const summary = [
        '╔══════════════════════════════════════════════════════════╗',
        '  WILSAPP NOTARISDOSSIER - SAMENVATTING',
        '╚══════════════════════════════════════════════════════════╝',
        `  Voorbereiding:       ${statusPct}% compleet`,
        ...(statusAandacht.length ? [`  Nog aandacht nodig:  ${statusAandacht.join(', ')}`] : []),
        '',
        `  Naam:                ${name}`,
        `  Partner:             ${partner?.hasPartner === 'yes' ? partnerNaam : 'Geen partner'}`,
        `  Kinderen:            ${children?.hasChildren === 'yes' ? (children.children?.length || 0) + ' kind(eren)' : 'Geen'}`,
        `  Vertrouwenspersoon:  ${primaryVP ? primaryVP.firstName : 'Niet ingevuld'}`,
        `  Onderneming:         ${answers.hasBusiness ? 'Ja' : 'Nee'}`,
        `  Koopwoning:          ${answers.hasOwnHome ? 'Ja' : 'Nee'}`,
        `  Tweede woning:       ${answers.hasSecondHome ? 'Ja' : 'Nee'}`,
        `  Testament gewenst:   ${answers.hasTestament ? 'Ja' : answers.hasTestament === false ? 'Nee' : 'Niet beantwoord'}`,
        `  Levenstestament:     ${answers.hasLivingWill ? 'Ja' : answers.hasLivingWill === false ? 'Nee' : 'Niet beantwoord'}`,
        `  Open aandachtspunten: ${punten.length}`,
        `  Exportdatum:         ${datum()}`,
        `  Documentversie:      1.0`,
      ].join('\n')

      const footer = [
        '',
        '═'.repeat(60),
        'Gegenereerd door WilsApp',
        `Exportdatum: ${datum()}`,
        '',
        'Dit document is bedoeld als voorbereiding voor gesprekken met',
        'een notaris. WilsApp vervangt geen notariële akte of juridisch advies.',
        'De inhoud is vastgelegd door de gebruiker zelf en heeft geen',
        'officiële juridische of medische status.',
        '═'.repeat(60),
      ].join('\n')

      const volgendeStap = [
        '',
        'VOLGENDE STAP',
        '─'.repeat(13),
        'Dit dossier is bedoeld als voorbereiding op een gesprek met uw notaris.',
        'Bespreek tijdens dit gesprek onder andere:',
        '  • testament',
        '  • executeur',
        '  • voogdij',
        '  • erfgenamen',
        '  • bijzondere wensen',
        '  • levenstestament',
        '',
        'Na ondertekening bij de notaris worden deze documenten - indien van',
        'toepassing - officieel en juridisch vastgelegd.',
      ].join('\n')

      const fullText = [summary, ch1, ch2, ch3, ch4, ch5, ch6, ch7, ch8, ch9a, ch9, ch10, ch11, footer, volgendeStap].join('\n')
      // Professionele export data (parallel aan dossierText)
      const exportSections: ExportSection[] = [
        { title: 'Persoonlijke situatie', items: [
          { label: 'Naam', value: name },
          { label: 'Adres', value: [profileAddr?.street, profileAddr?.houseNumber, profileAddr?.city].filter(Boolean).join(' ') },
          { label: 'Partner', value: partner?.hasPartner === 'yes' ? partnerNaam : 'Geen partner' },
          { label: 'Huwelijkse voorwaarden', value: notarial?.huwelijkseVoorwaarden === 'yes' ? 'Ja' : notarial?.huwelijkseVoorwaarden === 'no' ? 'Nee' : 'Niet beantwoord' },
        ]},
        { title: 'Testament en nalatenschap', items: [
          { label: 'Testament', value: answers.hasTestament ? 'Aanwezig' : answers.hasTestament === false ? 'Nee' : 'Niet beantwoord' },
          { label: 'Levenstestament', value: answers.hasLivingWill ? 'Aanwezig' : answers.hasLivingWill === false ? 'Nee' : 'Niet beantwoord' },
          { label: 'Executeur', value: notarial?.executeur?.name || '', warn: !notarial?.executeur?.name },
          { label: 'Tweede executeur', value: notarial?.executeur?.reserveName || '' },
          { label: 'Voogd kinderen', value: notarial?.voogdij?.name || '' },
        ]},
        { title: 'Medische vertegenwoordiging', items: [
          { label: 'Medisch vertegenwoordiger', value: medical?.proxyDecisionMaker || (medical?.proxySource ? 'Zie vertrouwenspersoon' : ''), warn: !medical?.proxyDecisionMaker && !medical?.proxySource },
          { label: 'Tweede vertegenwoordiger', value: medical?.reserveProxyName || '' },
        ]},
        { title: 'Uitvaartwensen', items: [
          { label: 'Type uitvaart', value: funeral?.type === 'natural' ? 'Natuurbegraving' : funeral?.type === 'cremation' ? 'Crematie' : funeral?.type === 'burial' ? 'Begraving' : '' },
          { label: 'Locatiewens', value: nl(funeral?.locationWish) || nl(funeral?.locationVenue) },
          { label: 'Uitvaartverzorger', value: funeral?.funeralHome || '' },
        ]},
      ]
      setExportData({
        type: 'notaris', firstName: name, exportDate: datum(), version: '1.0',
        sections: exportSections,
        alertItems: punten.map(p => p.label),
        summaryItems: [
          'Neem contact op met de vertrouwenspersoon',
          'Raadpleeg de notaris voor formele vastlegging',
          'Controleer of het testament actueel is',
          'Informeer nabestaanden over de locatie van dit dossier',
        ],
      })
      setDossierText(fullText)
      setIsLoading(false)
    }
    load()
  }, [userId, answers])

  async function handleDownloadDocx() {
    if (!exportData) return
    setGenerated(true)
    const { generateDocx, downloadDocx } = await import('@/lib/export/generateDocx')
    const blob = await generateDocx(exportData)
    downloadDocx(blob, `WilsApp-Notarisdossier-${firstName || 'export'}-${datum().replace(/\//g,'-')}.docx`)
  }

  async function handleDownloadPdf() {
    if (!exportData) return
    setGenerated(true)
    const { generatePdf, downloadPdf } = await import('@/lib/export/generatePdf')
    const blob = await generatePdf(exportData)
    downloadPdf(blob, `WilsApp-Notarisdossier-${firstName || 'export'}-${datum().replace(/\//g,'-')}.pdf`)
  }

  function handlePrint() {
    setGenerated(true)
    setTimeout(() => window.print(), 300)
  }

  function handleEmail() {
    setGenerated(true)
    const body = [
      'Geachte notaris,',
      '',
      'Hierbij stuur ik mijn voorbereidend notarisdossier vanuit WilsApp.',
      'Dit document bevat mijn wensen, contactpersonen en aandachtspunten ter voorbereiding op een gesprek.',
      '',
      'WilsApp vervangt geen juridisch advies of notariële akte.',
      '',
      'Met vriendelijke groet,',
      firstName || '(uw naam)',
      '',
      '---',
      dossierText,
    ].join('\n')
    const subject = encodeURIComponent('Voorbereidend notarisdossier WilsApp')
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(body)}`
  }

  function handleCopy() {
    navigator.clipboard.writeText(dossierText).catch(() => {
      const el = document.createElement('textarea')
      el.value = dossierText; document.body.appendChild(el); el.select()
      document.execCommand('copy'); document.body.removeChild(el)
    })
    setCopied(true); setTimeout(() => setCopied(false), 2500)
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <button onClick={() => navigate(ROUTES.DASHBOARD)} className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors">
        <ArrowLeft className="h-4 w-4" />Terug naar dashboard
      </button>

      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 flex-shrink-0">
          <Scale className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink">Notarisdossier</h1>
          <p className="text-xs text-ink-4">Professioneel overzicht voor uw notaris - 11 hoofdstukken</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleDownloadDocx} icon={<Download className="h-4 w-4" />}>Word (.docx)</Button>
        <Button onClick={handleDownloadPdf} variant="secondary" icon={<Download className="h-4 w-4" />}>PDF</Button>
        <Button variant="secondary" onClick={handlePrint} icon={<Printer className="h-4 w-4" />}>Printen</Button>
        <Button variant="secondary" onClick={handleEmail} icon={<Mail className="h-4 w-4" />}>E-mail naar notaris</Button>
        <Button variant="outline" onClick={handleCopy} icon={copied ? <CheckCircle2 className="h-4 w-4 text-sage-600" /> : <Copy className="h-4 w-4" />}>
          {copied ? 'Gekopieerd!' : 'Dossier kopiëren'}
        </Button>
      </div>

      {generated && (
        <div className="text-xs text-sage-700 bg-sage-50 border border-sage-200 rounded-xl px-4 py-2.5">
          ✓ Dossier gereed. Kies een actie hierboven. Geen gegevens worden automatisch verstuurd - u bent altijd zelf de verzender.
        </div>
      )}

      <div className="rounded-xl bg-sand border border-sand-300 px-4 py-3 text-xs text-ink-4 leading-relaxed">
        <strong className="text-ink-3">Let op:</strong> Dit dossier is een overzicht van uw wensen in WilsApp. Het is geen officieel juridisch document. Raadpleeg altijd een notaris voor bindende vastlegging.
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-4">Dossier samenstellen...</p>
      ) : (
        <div className="rounded-2xl bg-white border border-sand-300 shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-sand-200 bg-sand-50">
            <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider">Voorvertoning dossier</p>
          </div>
          <pre className="px-5 py-4 text-xs text-ink leading-relaxed font-mono whitespace-pre-wrap overflow-x-auto max-h-[60vh] overflow-y-auto">
            {dossierText}
          </pre>
        </div>
      )}
    </div>
  )
}
